<template>
  <div class="imei-scanner-wrap">
    <!-- Camera button -->
    <div class="imei-scan-btn" title="Scan IMEI from photo" @click="triggerFile">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
    </div>
    <input ref="fileInput" type="file" accept="image/*" capture="environment" style="display:none" @change="onFileChange" />

    <!-- These get moved to <body> on mount so position:fixed always works -->
    <div ref="portalContainer" style="display:none">
      <!-- Loading overlay -->
      <div v-if="loading" class="imei-scan-loading">
        <div class="imei-scan-loading-inner">
          <div class="imei-scan-spinner"></div>
          <div class="imei-scan-loading-text">{{ loadingText }}</div>
          <button class="imei-scan-loading-cancel" @click="cancelScan">Cancel</button>
        </div>
      </div>

      <!-- Picker modal -->
      <div v-if="showModal" class="imei-scan-modal-bg" @click.self="closeModal">
        <div class="imei-scan-modal">
          <div class="imei-scan-modal-handle"></div>
          <div class="imei-scan-modal-title">{{ entries.length }} IMEI{{ entries.length > 1 ? 's' : '' }} detected</div>
          <div class="imei-scan-modal-sub">Select the correct IMEI</div>

          <div
            v-for="(item, i) in sortedEntries"
            :key="item.imei"
            class="imei-scan-pick"
            :class="{ selected: selected === item.imei }"
            @click="selected = item.imei"
          >
            <span class="imei-scan-pick-num">{{ fmtIMEI(item.imei) }}</span>
            <span class="imei-scan-pick-badge" :class="{ barcode: item.source === 'barcode' }">
              {{ item.source === 'barcode' ? '⊟ Barcode' : '✓ Valid' }}
            </span>
          </div>

          <div v-if="sortedEntries.length >= 2" class="imei-scan-tip">
            <strong>Tip:</strong> For dual-IMEI devices, use <strong>IMEI 1</strong> for checks.
          </div>

          <div class="imei-scan-modal-btns">
            <button class="imei-scan-mbtn imei-scan-mbtn-cancel" @click="closeModal">Cancel</button>
            <button class="imei-scan-mbtn imei-scan-mbtn-use" :disabled="!selected" @click="useSelected">
              {{ selected ? 'Use ' + fmtIMEI(selected) : 'Use' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="toast-fade">
        <div v-if="toast" class="imei-scan-toast">{{ toast }}</div>
      </transition>
    </div>
  </div>
</template>

<script>
const TESSERACT_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/tesseract.js/5.0.4/tesseract.min.js'
const BARCODE_CDN = 'https://fastly.jsdelivr.net/npm/barcode-detector@3/dist/iife/index.min.js'

let _ocrWorker = null
let _ocrReady = false
let _barcodeDetector = null
let _barcodeLoading = null

function luhn(imei) {
  if (!/^\d{15}$/.test(imei)) return false
  let s = 0
  for (let i = 0; i < 15; i++) {
    let d = +imei[i]
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9 }
    s += d
  }
  return s % 10 === 0
}

function validPrefix(imei) {
  return /^(35|01|86)/.test(imei)
}

function ocrFix(text) {
  return text
    .replace(/(\d)%(\d)/g, '$19$2')
    .replace(/(\d)[oO](\d)/g, '$10$2')
    .replace(/(\d)[lI|](\d)/g, '$11$2')
    .replace(/(\d)[Ss](\d)/g, '$15$2')
    .replace(/(\d)[Bb](\d)/g, '$18$2')
    .replace(/(\d)[Gg](\d)/g, '$19$2')
    .replace(/(\d)[Zz](\d)/g, '$12$2')
}

function extractIMEIs(text) {
  const results = new Map()
  for (const rawLine of text.split(/[\n\r]+/)) {
    const line = ocrFix(rawLine)
    const groups = line.match(/\d+/g)
    if (!groups) continue
    for (const g of groups) {
      if (g.length === 15 && validPrefix(g) && luhn(g)) results.set(g, true)
    }
    for (let start = 0; start < groups.length; start++) {
      let combined = ''
      for (let end = start; end < groups.length; end++) {
        combined += groups[end]
        if (combined.length > 20) break
        if (combined.length >= 15) {
          for (let i = 0; i <= combined.length - 15; i++) {
            const c = combined.substring(i, i + 15)
            if (validPrefix(c) && luhn(c)) results.set(c, true)
          }
          break
        }
      }
    }
  }
  return [...results.keys()]
}

function extractIMEIsFromBarcode(rawValue) {
  if (!rawValue) return []
  const digits = rawValue.replace(/\D/g, '')
  const found = []
  if (digits.length === 15 && validPrefix(digits) && luhn(digits)) found.push(digits)
  if (digits.length > 15) {
    for (let i = 0; i <= digits.length - 15; i++) {
      const c = digits.substring(i, i + 15)
      if (validPrefix(c) && luhn(c) && !found.includes(c)) found.push(c)
    }
  }
  return found
}

function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(true); return }
    const s = document.createElement('script')
    s.src = src
    s.onload = () => resolve(true)
    s.onerror = () => resolve(false)
    document.head.appendChild(s)
  })
}

function loadBarcodeDetector() {
  if (_barcodeDetector) return Promise.resolve(true)
  if (_barcodeLoading) return _barcodeLoading
  _barcodeLoading = (async () => {
    await loadScript(BARCODE_CDN)
    try {
      // Always use polyfill (zxing-wasm) — native BarcodeDetector is unreliable
      const BD = (window.BarcodeDetectionAPI && window.BarcodeDetectionAPI.BarcodeDetector) || window.BarcodeDetector
      if (BD) {
        _barcodeDetector = new BD({
          formats: ['code_128', 'code_39', 'code_93', 'ean_13', 'ean_8', 'itf', 'codabar', 'upc_a', 'upc_e']
        })
        // Warmup: trigger WASM load so first real scan is instant
        try {
          const c = document.createElement('canvas')
          c.width = 1; c.height = 1
          await _barcodeDetector.detect(c)
        } catch (e) { /* warmup error is fine */ }
        return true
      }
    } catch (e) { console.error('[ImeiScanner] BarcodeDetector init error:', e) }
    return false
  })()
  return _barcodeLoading
}

async function scanBarcode(imgEl) {
  if (!_barcodeDetector) return []
  try {
    const barcodes = await _barcodeDetector.detect(imgEl)
    const imeis = []
    for (const b of barcodes) {
      for (const imei of extractIMEIsFromBarcode(b.rawValue)) {
        if (!imeis.includes(imei)) imeis.push(imei)
      }
    }
    return imeis
  } catch (e) { return [] }
}

function toGray(source) {
  const c = document.createElement('canvas')
  c.width = source.width; c.height = source.height
  const ctx = c.getContext('2d'); ctx.drawImage(source, 0, 0)
  const img = ctx.getImageData(0, 0, c.width, c.height); const d = img.data
  for (let i = 0; i < d.length; i += 4) { const g = Math.round(.299*d[i]+.587*d[i+1]+.114*d[i+2]); d[i]=d[i+1]=d[i+2]=g }
  ctx.putImageData(img, 0, 0); return c
}
function toContrast(source) {
  const c = document.createElement('canvas')
  c.width = source.width; c.height = source.height
  const ctx = c.getContext('2d'); ctx.drawImage(source, 0, 0)
  const img = ctx.getImageData(0, 0, c.width, c.height); const d = img.data
  for (let i = 0; i < d.length; i += 4) { let g=.299*d[i]+.587*d[i+1]+.114*d[i+2]; g=Math.max(0,Math.min(255,((g-128)*2)+128)); d[i]=d[i+1]=d[i+2]=g }
  ctx.putImageData(img, 0, 0); return c
}
function toBinary(source, t) {
  const c = document.createElement('canvas')
  c.width = source.width; c.height = source.height
  const ctx = c.getContext('2d'); ctx.drawImage(source, 0, 0)
  const img = ctx.getImageData(0, 0, c.width, c.height); const d = img.data
  for (let i = 0; i < d.length; i += 4) { const g=.299*d[i]+.587*d[i+1]+.114*d[i+2]; const v=g>t?255:0; d[i]=d[i+1]=d[i+2]=v }
  ctx.putImageData(img, 0, 0); return c
}
function toInvertBinary(source, t) {
  const c = document.createElement('canvas')
  c.width = source.width; c.height = source.height
  const ctx = c.getContext('2d'); ctx.drawImage(source, 0, 0)
  const img = ctx.getImageData(0, 0, c.width, c.height); const d = img.data
  for (let i = 0; i < d.length; i += 4) { const g=255-Math.round(.299*d[i]+.587*d[i+1]+.114*d[i+2]); const v=g>t?255:0; d[i]=d[i+1]=d[i+2]=v }
  ctx.putImageData(img, 0, 0); return c
}
function toInvertSharpBinary(source, t) {
  const c = document.createElement('canvas')
  c.width = source.width; c.height = source.height
  const ctx = c.getContext('2d'); ctx.drawImage(source, 0, 0)
  const img = ctx.getImageData(0, 0, c.width, c.height); const d = img.data
  const w = c.width, h = c.height
  const gray = new Float32Array(w * h)
  for (let i = 0; i < d.length; i += 4) gray[i/4] = 255-(.299*d[i]+.587*d[i+1]+.114*d[i+2])
  const out = new Float32Array(w * h)
  for (let y = 1; y < h-1; y++) for (let x = 1; x < w-1; x++) { const idx=y*w+x; out[idx]=gray[idx]*5-gray[idx-1]-gray[idx+1]-gray[idx-w]-gray[idx+w] }
  for (let i = 0; i < d.length; i += 4) { const v=out[i/4]>t?255:0; d[i]=d[i+1]=d[i+2]=v; d[i+3]=255 }
  ctx.putImageData(img, 0, 0); return c
}

export default {
  name: 'ImeiScanner',
  data() {
    return {
      loading: false,
      loadingText: 'Scanning barcode...',
      showModal: false,
      entries: [],
      selected: null,
      toast: null,
      cancelled: false,
    }
  },
  computed: {
    sortedEntries() {
      return [...this.entries].sort((a, b) => a.imei < b.imei ? -1 : a.imei > b.imei ? 1 : 0)
    },
  },
  mounted() {
    // Move overlays to <body> so position:fixed works regardless of parent transforms/overflow
    const portal = this.$refs.portalContainer
    if (portal) {
      portal.style.display = ''
      document.body.appendChild(portal)
    }
    // Preload barcode detector + warmup WASM
    loadBarcodeDetector()
  },
  beforeDestroy() {
    const portal = this.$refs.portalContainer
    if (portal && portal.parentNode === document.body) {
      document.body.removeChild(portal)
    }
  },
  methods: {
    fmtIMEI(imei) {
      return imei.replace(/(\d{2})(\d{6})(\d{6})(\d)/, '$1 $2 $3 $4')
    },
    triggerFile() {
      this.$refs.fileInput.click()
    },
    onFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      e.target.value = ''
      this.processPhoto(file)
    },
    async processPhoto(file) {
      // Blur keyboard on mobile
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur()

      this.cancelled = false
      this.loading = true
      this.loadingText = 'Scanning barcode...'

      try {
        // Load image + barcode + tesseract in parallel
        const imgPromise = this.loadImage(file)
        const barcodePromise = loadBarcodeDetector()
        const tesseractPromise = loadScript(TESSERACT_CDN)

        const imgEl = await imgPromise
        if (this.cancelled) return

        await barcodePromise
        if (this.cancelled) return

        // Phase 1: Barcode
        const barcodeIMEIs = await scanBarcode(imgEl)
        if (this.cancelled) return

        if (barcodeIMEIs.length > 0) {
          this.loading = false
          const entries = barcodeIMEIs.map(imei => ({ imei, source: 'barcode' }))
          if (entries.length === 1) {
            this.$emit('scanned', entries[0].imei)
            this.showToast('IMEI detected from barcode!')
          } else {
            this.entries = entries
            this.selected = this.sortEntries(entries)[0].imei
            this.showModal = true
          }
          return
        }

        // Phase 2: OCR
        this.loadingText = 'Almost there...'
        await tesseractPromise
        if (this.cancelled) return

        if (!window.Tesseract) { this.loading = false; this.showToast('OCR engine failed to load.'); return }
        if (!_ocrReady) {
          _ocrWorker = await window.Tesseract.createWorker('eng', 1, { logger() {} })
          await _ocrWorker.setParameters({ tessedit_pageseg_mode: '6' })
          _ocrReady = true
        }

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        let w = imgEl.naturalWidth || imgEl.width
        let h = imgEl.naturalHeight || imgEl.height
        if (w > 1200) { h = Math.round(h * 1200 / w); w = 1200 }
        canvas.width = w; canvas.height = h
        ctx.drawImage(imgEl, 0, 0, w, h)

        const variants = [s => toBinary(s,150), toContrast, s => toInvertBinary(s,80)]
        const variantMessages = ['Reading label...', 'Adjusting brightness...', 'Trying harder...']
        const found = new Map()
        for (let i = 0; i < variants.length; i++) {
          if (this.cancelled) return
          this.loadingText = variantMessages[i]
          try {
            const processed = variants[i](canvas)
            const r = await _ocrWorker.recognize(processed)
            const text = r.data.text || ''
            if (text.trim()) {
              for (const imei of extractIMEIs(text)) found.set(imei, (found.get(imei)||0)+1)
              if (found.size > 0) break
            }
          } catch (e) {}
        }

        this.loading = false
        const ocrEntries = []
        found.forEach((count, imei) => ocrEntries.push({ imei, source: 'ocr' }))

        if (ocrEntries.length === 1) {
          this.$emit('scanned', ocrEntries[0].imei)
          this.showToast('IMEI detected!')
        } else if (ocrEntries.length > 1) {
          this.entries = ocrEntries
          this.selected = this.sortEntries(ocrEntries)[0].imei
          this.showModal = true
        } else {
          this.showToast('No valid IMEI detected. Try a clearer photo.')
        }
      } catch (err) {
        console.error('[ImeiScanner]', err)
        this.loading = false
        this.showToast('Error processing image.')
      }
    },
    sortEntries(entries) {
      return [...entries].sort((a, b) => a.imei < b.imei ? -1 : a.imei > b.imei ? 1 : 0)
    },
    loadImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        const url = URL.createObjectURL(file)
        img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')) }
        img.src = url
      })
    },
    closeModal() {
      this.showModal = false
      this.entries = []
      this.selected = null
    },
    cancelScan() {
      this.cancelled = true
      this.loading = false
    },
    useSelected() {
      if (this.selected) {
        this.$emit('scanned', this.selected)
        this.closeModal()
      }
    },
    showToast(msg) {
      this.toast = msg
      if (navigator.vibrate) navigator.vibrate([40, 20, 40])
      setTimeout(() => { this.toast = null }, 3500)
    },
  },
}
</script>

<style>
/* Button stays scoped via class names */
.imei-scanner-wrap { display: inline-flex; align-items: center; }
.imei-scan-btn {
  width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
  border-radius: 6px; cursor: pointer; color: #b0b0b0; transition: all .15s; overflow: hidden; position: relative;
}
.imei-scan-btn:hover { color: #007bff; background: rgba(0,123,255,.08); }
.imei-scan-btn:active { transform: scale(.9); }
.imei-scan-btn svg { width: 16px; height: 16px; pointer-events: none; }

.imei-scan-loading {
  display: flex; position: fixed; inset: 0; z-index: 10000;
  background: rgba(255,255,255,.88); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  align-items: center; justify-content: center;
}
.imei-scan-loading-inner { text-align: center; }
.imei-scan-spinner {
  width: 36px; height: 36px; margin: 0 auto 14px;
  border: 3px solid #e0e0e0; border-top-color: #007bff; border-radius: 50%;
  animation: imeiSpin .8s linear infinite;
}
@keyframes imeiSpin { to { transform: rotate(360deg) } }
.imei-scan-loading-text { font-size: 14px; color: #888; font-weight: 500; }
.imei-scan-loading-cancel {
  margin-top: 18px; padding: 10px 32px; border: none; border-radius: 10px;
  background: #f0f0f2; color: #888; font-size: 14px; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: transform .1s;
}
.imei-scan-loading-cancel:active { transform: scale(.95); }

.imei-scan-modal-bg {
  display: flex; position: fixed; inset: 0; z-index: 10001;
  background: rgba(0,0,0,.45); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
  align-items: flex-end; justify-content: center;
}
.imei-scan-modal {
  background: #fff; width: 100%; max-width: 480px;
  border-radius: 18px 18px 0 0; padding: 20px 18px calc(28px + env(safe-area-inset-bottom, 0px));
  max-height: 70vh; overflow-y: auto;
  animation: imeiSlideUp .35s cubic-bezier(.2,.8,.2,1);
}
@keyframes imeiSlideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
.imei-scan-modal-handle { width: 36px; height: 4px; background: #ddd; border-radius: 2px; margin: 0 auto 16px; }
.imei-scan-modal-title { font-size: 17px; font-weight: 700; margin-bottom: 4px; color: #222; }
.imei-scan-modal-sub { font-size: 13px; color: #999; margin-bottom: 14px; }

.imei-scan-pick {
  background: #f8f8fa; border: 1.5px solid #e5e5ea; border-radius: 12px;
  padding: 16px 16px; margin-bottom: 8px; display: flex; align-items: center; justify-content: space-between;
  cursor: pointer; transition: all .15s; min-height: 54px;
}
.imei-scan-pick:active { transform: scale(.98); }
.imei-scan-pick.selected { border-color: #007bff; background: rgba(0,123,255,.06); }
.imei-scan-pick-num { font-family: "Consolas","Monaco",monospace; font-size: 16px; font-weight: 600; letter-spacing: .04em; color: #222; }
.imei-scan-pick-badge { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 6px; background: rgba(52,199,89,.1); color: #34c759; }
.imei-scan-pick-badge.barcode { background: rgba(0,123,255,.1); color: #007bff; }

.imei-scan-modal-btns { display: flex; gap: 10px; margin-top: 14px; }
.imei-scan-tip { font-size: 11px; color: #999; line-height: 1.5; padding: 8px 12px; margin-top: 4px; background: rgba(0,123,255,.04); border-radius: 8px; border-left: 2px solid rgba(0,123,255,.3); }
.imei-scan-tip strong { color: #777; }
.imei-scan-mbtn { flex: 1; padding: 15px; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: transform .1s; font-family: inherit; min-height: 50px; }
.imei-scan-mbtn:active { transform: scale(.97); }
.imei-scan-mbtn-cancel { background: #f0f0f2; color: #888; }
.imei-scan-mbtn-use { background: #007bff; color: #fff; }
.imei-scan-mbtn-use:disabled { opacity: .4; }

.imei-scan-toast {
  position: fixed; top: calc(20px + env(safe-area-inset-top, 0px)); left: 50%; transform: translateX(-50%);
  background: #333; color: #fff; padding: 12px 24px; border-radius: 12px;
  font-size: 14px; font-weight: 500; z-index: 99999;
  box-shadow: 0 4px 20px rgba(0,0,0,.15); max-width: 90%; text-align: center;
}
.toast-fade-enter-active, .toast-fade-leave-active { transition: all .4s cubic-bezier(.2,.8,.2,1); }
.toast-fade-enter, .toast-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(-80px); }
</style>